import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  X,
  Layout,
  Users,
  Globe,
  User,
  Target,
  TrendingUp,
  ClipboardList,
  DollarSign,
  Calendar,
} from 'lucide-react';
import {
  useGetDashboardsQuery,
  useCreateDashboardMutation,
  useSetDefaultDashboardMutation,
} from '../../services/dashboardApi';
import type { Dashboard, CreateDashboardRequest } from '../../types/dashboard';
import type { TeamRole } from '../../types';
import { Input, Dropdown, MultiDropdown } from '../forms';
import { useAuth } from '../../hooks/useAuth';

// Available roles for dashboard sharing
const AVAILABLE_ROLES: { value: TeamRole; label: string; description: string }[] = [
  { value: 'owner', label: 'Project Owner', description: 'Full project control and oversight' },
  {
    value: 'project_manager',
    label: 'Project Manager',
    description: 'Manages daily operations and coordination',
  },
  { value: 'architect', label: 'Architect', description: 'Design and planning oversight' },
  { value: 'engineer', label: 'Engineer', description: 'Technical design and implementation' },
  { value: 'contractor', label: 'Contractor', description: 'Construction execution and trades' },
  {
    value: 'site_supervisor',
    label: 'Site Supervisor',
    description: 'On-site operations and safety',
  },
  { value: 'client', label: 'Client', description: 'Project stakeholder and decision maker' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to project information' },
];

// Dashboard purpose templates for quick setup
const DASHBOARD_PURPOSES = [
  {
    id: 'progress',
    name: 'Progress Tracking',
    icon: Target,
    description: 'Track milestones, tasks completion, and project timeline',
    suggestedName: 'Progress Dashboard',
    color: 'bg-blue-100 text-blue-600',
  },
  {
    id: 'performance',
    name: 'Performance Overview',
    icon: TrendingUp,
    description: 'Monitor team productivity, KPIs, and efficiency metrics',
    suggestedName: 'Performance Dashboard',
    color: 'bg-green-100 text-green-600',
  },
  {
    id: 'tasks',
    name: 'Task Management',
    icon: ClipboardList,
    description: 'Focus on task lists, assignments, and deadlines',
    suggestedName: 'Tasks Dashboard',
    color: 'bg-purple-100 text-purple-600',
  },
  {
    id: 'financial',
    name: 'Financial Overview',
    icon: DollarSign,
    description: 'Track budgets, costs, invoices, and financial metrics',
    suggestedName: 'Financial Dashboard',
    color: 'bg-yellow-100 text-yellow-600',
  },
  {
    id: 'schedule',
    name: 'Schedule & Calendar',
    icon: Calendar,
    description: 'View meetings, deadlines, and project timeline',
    suggestedName: 'Schedule Dashboard',
    color: 'bg-orange-100 text-orange-600',
  },
  {
    id: 'custom',
    name: 'Custom Dashboard',
    icon: Layout,
    description: 'Start from scratch and add your own widgets',
    suggestedName: '',
    color: 'bg-gray-100 text-gray-600',
  },
];

// Validation schema
const createDashboardSchema = yup.object({
  name: yup
    .string()
    .required('Dashboard name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be less than 100 characters')
    .trim(),
  description: yup.string().max(500, 'Description must be less than 500 characters').optional(),
  purpose: yup.string().optional(),
  visibility: yup
    .string()
    .oneOf(['personal', 'role', 'public'])
    .required('Visibility type is required'),
  roleIds: yup.array().when('visibility', {
    is: 'role',
    then: schema =>
      schema.min(1, 'At least one role must be selected for role-based dashboards').required(),
    otherwise: schema => schema.optional(),
  }),
  copyFromId: yup.string().optional(),
  setAsDefault: yup.boolean().optional(),
});

type CreateDashboardFormData = yup.InferType<typeof createDashboardSchema>;

export interface CreateDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (dashboard: Dashboard) => void;
  projectId?: string;
}

export const CreateDashboardModal: React.FC<CreateDashboardModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  projectId,
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch existing dashboards for copy options (include project context if available)
  const { data: dashboardsResponse, isLoading: isDashboardsLoading } = useGetDashboardsQuery(
    projectId ? { projectId } : undefined
  );
  const dashboards = dashboardsResponse?.data || [];

  const [createDashboard] = useCreateDashboardMutation();
  const [setDefaultDashboard] = useSetDefaultDashboardMutation();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    trigger,
    getValues,
    formState: { errors, isValid, isSubmitting: formIsSubmitting },
  } = useForm<CreateDashboardFormData>({
    resolver: yupResolver(createDashboardSchema) as any,
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      description: '',
      purpose: 'custom',
      visibility: 'personal',
      roleIds: [],
      copyFromId: '',
      setAsDefault: false,
    },
  });

  const watchedVisibility = watch('visibility');
  const watchedName = watch('name');
  const watchedPurpose = watch('purpose');

  // Handle purpose selection - auto-fill name if empty
  const handlePurposeSelect = (purposeId: string) => {
    setValue('purpose', purposeId);
    const purpose = DASHBOARD_PURPOSES.find(p => p.id === purposeId);
    if (purpose?.suggestedName && !watchedName) {
      setValue('name', purpose.suggestedName);
    }
  };

  // Template options for copy from dropdown
  // Use empty string instead of null for "no template" to work with Dropdown component
  const templateOptions = [
    { value: '', label: 'Blank Dashboard', description: 'Start with an empty dashboard' },
    ...dashboards
      .filter(
        dashboard =>
          dashboard.visibility === 'public' ||
          dashboard.visibility === 'role' ||
          (dashboard.visibility === 'personal' && dashboard.ownerUserId === user?.id)
      )
      .map(dashboard => ({
        value: dashboard._id,
        label: dashboard.name,
        description: `Copy widgets from "${dashboard.name}" (${dashboard.visibility})`,
      })),
  ];

  const roleOptions = AVAILABLE_ROLES.map(role => ({
    value: role.value,
    label: role.label,
    description: role.description,
  }));

  const handleFormSubmit = async (data: CreateDashboardFormData) => {
    console.log('handleFormSubmit called with data:', data);
    console.log('User object:', user);
    // Handle both 'id' and '_id' from backend
    const userId = user?.id || (user as any)?._id;
    if (!userId) {
      console.error('No user ID available, user object:', user);
      setError('User session not found. Please refresh the page and try again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare request data
      // Convert empty string copyFromId to undefined for the API
      const requestData: CreateDashboardRequest = {
        name: data.name,
        description: data.description || undefined,
        purpose: data.purpose || undefined,
        visibility: data.visibility as 'personal' | 'role' | 'public',
        roleIds: data.visibility === 'role' ? data.roleIds : undefined,
        copyFromId: data.copyFromId || undefined,
        setAsDefault: data.visibility === 'personal' ? data.setAsDefault : false,
        projectId: projectId, // Include project context for project-scoped dashboards
      };

      // Create dashboard
      const result = await createDashboard(requestData).unwrap();
      const newDashboard = result.data;

      // If setting as default for personal dashboard, update user preferences
      if (data.visibility === 'personal' && data.setAsDefault && newDashboard._id) {
        await setDefaultDashboard({
          userId: userId,
          data: { dashboardId: newDashboard._id },
        });
      }

      // Success feedback and cleanup
      reset();
      setError(null);
      onSuccess?.(newDashboard);
      onClose();
    } catch (err: any) {
      console.error('Failed to create dashboard:', err);
      setError(
        err?.data?.message || err?.message || 'Failed to create dashboard. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  // Check if user can create public/role dashboards
  const canCreatePublicDashboards = user?.role === 'admin';

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[600px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden brand-form min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <Layout className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Create Dashboard</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {projectId
                    ? 'Create a purpose-specific dashboard to track project data'
                    : 'Add a new dashboard to organize your data'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form
            onSubmit={handleSubmit(handleFormSubmit, validationErrors => {
              console.error('Form validation errors:', validationErrors);
              setError('Please fix the form errors and try again.');
            })}
            className="flex-1 flex flex-col min-h-0"
          >
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 min-h-0">
              {/* Error Display */}
              {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Dashboard Purpose */}
              {projectId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Dashboard Purpose
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {DASHBOARD_PURPOSES.map(purpose => {
                      const Icon = purpose.icon;
                      const isSelected = watchedPurpose === purpose.id;
                      return (
                        <button
                          key={purpose.id}
                          type="button"
                          onClick={() => handlePurposeSelect(purpose.id)}
                          disabled={isSubmitting}
                          className={`flex items-start space-x-3 p-3 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          } disabled:opacity-50`}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${purpose.color}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{purpose.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                              {purpose.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dashboard Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dashboard Name <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="e.g., Progress Tracker, Financial Overview..."
                      error={errors.name?.message}
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  )}
                />
              </div>

              {/* Dashboard Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      placeholder="Describe the purpose of this dashboard..."
                      disabled={isSubmitting}
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-sm text-red-600 mt-1">{errors.description.message}</p>
                )}
              </div>

              {/* Copy From Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template</label>
                <Controller
                  name="copyFromId"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={templateOptions}
                      placeholder="Choose a template..."
                      disabled={isSubmitting || isDashboardsLoading}
                      className="w-full"
                    />
                  )}
                />
              </div>

              {/* Visibility Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Dashboard Visibility <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="visibility"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-3">
                      {/* Personal */}
                      <label
                        className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          field.value === 'personal'
                            ? 'border-red-200 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value="personal"
                          checked={field.value === 'personal'}
                          onChange={() => field.onChange('personal')}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={isSubmitting}
                          className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                        />
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mt-0.5">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">For myself</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Private dashboard visible only to you
                            </p>
                          </div>
                        </div>
                      </label>

                      {/* Role */}
                      <label
                        className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          field.value === 'role'
                            ? 'border-red-200 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        } ${!canCreatePublicDashboards ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value="role"
                          checked={field.value === 'role'}
                          onChange={() => field.onChange('role')}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={isSubmitting || !canCreatePublicDashboards}
                          className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                        />
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mt-0.5">
                            <Users className="h-4 w-4 text-purple-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">For role(s)</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Shared with specific team roles
                              {!canCreatePublicDashboards && ' (Admin only)'}
                            </p>
                          </div>
                        </div>
                      </label>

                      {/* Public */}
                      <label
                        className={`flex items-start space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          field.value === 'public'
                            ? 'border-red-200 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        } ${!canCreatePublicDashboards ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name={field.name}
                          value="public"
                          checked={field.value === 'public'}
                          onChange={() => field.onChange('public')}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          disabled={isSubmitting || !canCreatePublicDashboards}
                          className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                        />
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mt-0.5">
                            <Globe className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">For anyone</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Public dashboard visible to all team members
                              {!canCreatePublicDashboards && ' (Admin only)'}
                            </p>
                          </div>
                        </div>
                      </label>
                    </div>
                  )}
                />
                {errors.visibility && (
                  <p className="text-sm text-red-600 mt-2">{errors.visibility.message}</p>
                )}
              </div>

              {/* Role Selection (conditional) */}
              {watchedVisibility === 'role' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Roles <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="roleIds"
                    control={control}
                    render={({ field }) => (
                      <MultiDropdown
                        value={field.value || []}
                        onChange={field.onChange}
                        options={roleOptions}
                        placeholder="Select roles that can access this dashboard..."
                        disabled={isSubmitting}
                        className="w-full"
                        maxHeight="200px"
                      />
                    )}
                  />
                  {errors.roleIds && (
                    <p className="text-sm text-red-600 mt-1">{errors.roleIds.message}</p>
                  )}
                </div>
              )}

              {/* Set as Default (conditional) */}
              {watchedVisibility === 'personal' && (
                <div className="flex items-start space-x-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                  <Controller
                    name="setAsDefault"
                    control={control}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        checked={field.value || false}
                        onChange={e => field.onChange(e.target.checked)}
                        disabled={isSubmitting}
                        className="mt-0.5 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                      />
                    )}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Set as my default dashboard</p>
                    <p className="text-xs text-gray-500 mt-1">
                      This dashboard will open automatically when you visit the dashboard section
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={async () => {
                    console.log('Submit button clicked');
                    console.log('Current form values:', getValues());
                    console.log('Current errors:', errors);
                    const isFormValid = await trigger();
                    console.log('Form is valid:', isFormValid);
                    if (isFormValid) {
                      handleFormSubmit(getValues());
                    } else {
                      console.log('Validation errors after trigger:', errors);
                      setError('Please fix the form errors and try again.');
                    }
                  }}
                  className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
                >
                  {isSubmitting ? 'Creating...' : 'Create Dashboard'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body
  );
};
